import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import { formatApiErrorDetail, useAuth } from "../lib/auth";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const nextPath = sp.get("next") || "/account";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.password || (!form.email && !form.phone)) {
      toast.error("Provide name, password, and either email or phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        password: form.password,
        ...(form.email ? { email: form.email.trim() } : {}),
        ...(form.phone ? { phone: form.phone.trim() } : {}),
      };
      const { data } = await api.post("/auth/register", payload);
      setAuth(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate(nextPath, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="register-page">
      <Seo title="Create Account" description="Create your RIZ N WASH account to book laundry pickups and manage weekly subscriptions." path="/register" noindex={true} />
      <Nav />
      <div className="max-w-md mx-auto px-6 md:px-10 pt-14">
        <div className="overline mb-3">Sign up</div>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Create your account.
        </h1>
        <p className="mt-3 text-rw-ink2">Just enough to remember your address and text you when the runner is close.</p>

        <div className="mt-8 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 space-y-4">
          <button
            onClick={startGoogleAuth}
            className="w-full flex items-center justify-center gap-3 rounded-full border border-rw-ink/20 py-3 hover:bg-rw-bg3 text-sm font-semibold"
            data-testid="google-signup-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 text-xs text-rw-ink3">
            <div className="flex-1 h-px bg-rw-ink/10" />
            OR
            <div className="flex-1 h-px bg-rw-ink/10" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="register-form">
            <label className="block">
              <span className="overline">Name</span>
              <input className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ananya Sharma" data-testid="register-name" />
            </label>
            <label className="block">
              <span className="overline">Email <span className="text-rw-ink3">(optional)</span></span>
              <input type="email" className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com" data-testid="register-email" />
            </label>
            <label className="block">
              <span className="overline">Phone <span className="text-rw-ink3">(optional)</span></span>
              <input className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 98xxx xxxxx" data-testid="register-phone" />
            </label>
            <p className="text-xs text-rw-ink3">Provide either email or phone — we&apos;ll use it to sign you in and send order updates.</p>
            <label className="block">
              <span className="overline">Password</span>
              <input type="password" className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 6 characters" data-testid="register-password" />
            </label>
            <button type="submit" disabled={loading}
              className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
              data-testid="register-submit-btn">
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>
        </div>

        <p className="mt-6 text-sm text-rw-ink2 text-center">
          Already have an account?{" "}
          <Link to={`/login${nextPath && nextPath !== "/account" ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="text-rw-ink font-semibold hover:underline" data-testid="link-to-login">
            Sign in
          </Link>
        </p>
      </div>
      <Footer />
    </div>
  );
}
